import React from "react";

import { trackAmplitudeEvent } from "../../amplitude";

import ShortcutsList from "./ShortcutsList";
import TextToSpeechSettings from "./V5TextToSpeechSettings";

const FontSizer = function({ label, onChange, value }) {
  const floatValue = parseFloat(value.replace(/-/, "."));
  return (
    <div className={`${label.toLowerCase().replace(/\W/, "-")}-font-sizer`} style={{ display: "flex", flexDirection: "row", marginTop: 10, textAlign: "left" }}>
      <div style={{ flex: 2 }}>
        {label}
      </div>
      <div style={{ flex: 1, textAlign: "center" }}>{floatValue}x</div>
      <div style={{ flex: 3, marginLeft: 10 }}>
        <input
          min="1"
          max="4"
          onChange={(e) => onChange(e.target.value.toString().replace(/\./, "-") + "x")}
          step="0.5"
          type="range"
          value={floatValue}
        />
        <div className={`font-size-${value}`}>Sample text.</div>
      </div>
    </div>
  );
};

// NOTE! be sure to update UserLanguagePairing whitelist when adding settings
export const DEFAULT_GAME_SETTINGS = {
  accentShortcuts: 'on',
  audioRecordings: 'on',
  clozemasterTextToSpeech: 'on',
  enterSubmitsEmpty: 'on',
  fitTextInputWidth: 'on',
  hints: 'on',
  hintFontSize: '1x',
  hotkeys: 'on',
  imageBackground: 'off',
  imageToggle: 'after',
  keyDownClearsTextInputAfterAnswering: 'on',
  leveledUpNotifications: 'on',
  listeningTextToSpeechVoice: 'random',
  listeningSkillAutoShowSentence: 'off',
  manualMasterResetConfirm: 'on',
  morePracticeControls: "off",
  notesFontSize: '1x',
  pronunciation: 'on',
  pronunciationFontSize: '1x',
  sentenceTextInitiallyHidden: "off",
  soundEffects: 'on',
  spellingHints: 'on',
  // Missing still means on (see isStrictDiacriticsSetting) for settings saved before
  // this existed.
  strictDiacritics: 'on',
  textToSpeech: 'on',
  textToSpeechSpeed: 1,
  textToSpeechVoice: 'random',
  textToSpeechLooping: 'off',
  theme: 'light',
  translationFontSize: '1x',
  translations: 'visible',
  transliteration: 'on',
  transliterationFontSize: '1x',
  typingColorHint: 'on',
};

export default class GameSettings extends React.Component {
  constructor(props) {
    super(props);

    this.state = Object.assign({}, DEFAULT_GAME_SETTINGS, props.initialSettings);
  }

  componentDidMount() {
    // GameSettings component sets default settings
    this.props.onUpdate && this.props.onUpdate(this.state);
  }

  componentDidUpdate(prevProps, prevState) {
    if(prevState.theme !== this.state.theme) {
      $('body').toggleClass('dark-mode', this.state.theme === 'dark');
    }
  }

  updateSetting(attr, value) {
    const { onUpdate, url } = this.props;

    let update = {};
    if(typeof attr === "object" && value == null) {
      update = attr;
    }
    else {
      update[attr] = value;
    }

    const newSettings = Object.assign({}, this.state, update);

    this.setState(newSettings, () => onUpdate && onUpdate(newSettings));

    $.ajax({
      url: url,
      data: {
        settings: newSettings
      },
      method: 'post'
    })
      .fail(() => {
        // TODO!
      });
  }

  renderBtnGroupBtn(attr, value, currentValue, content) {
    return (
      <button
        className={'btn ' + (currentValue === value ? 'btn-success active' : 'btn-default')}
        key={attr + '-' + value}
        onClick={() => this.updateSetting(attr, value)}
        type="button"
        value={value}
      >
        {content}
      </button>
    );
  }

  renderHints() {
    const hints = this.state.hints;
    return (
      <div className="setting-group hints">
        <div className="setting">
          <div className="header">Hints</div>
          <div className="description">(appear above the missing word when available)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('hints', 'on', hints, 'On')}
            {this.renderBtnGroupBtn('hints', 'off', hints, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderSpellingHints() {
    const spellingHints = this.state.spellingHints;
    return (
      <div className="setting-group spelling-hints">
        <div className="setting">
          <div className="header">Spelling Hints</div>
          <div className="description">(lets you know if your answer is incorrect by up to 2 letters)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('spellingHints', 'on', spellingHints, 'On')}
            {this.renderBtnGroupBtn('spellingHints', 'off', spellingHints, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  // Ported from mobile (CLZMSTR-1163), which keys the analytics on the setting name so
  // web and mobile toggles land in one chart.
  renderStrictDiacritics() {
    const { amplitudeProperties, strictDiacriticsCopy = {} } = this.props;
    const { strictDiacritics } = this.state;
    // Older saved settings have no value; those grade as strict, so show them as on.
    const value = strictDiacritics === "off" ? "off" : "on";

    const toggle = (newValue) => {
      if(newValue !== value) {
        trackAmplitudeEvent("web_app_game_settings_setting_toggled", Object.assign({}, amplitudeProperties, {
          setting: "strictDiacritics",
          value: newValue
        }));
      }
      this.updateSetting("strictDiacritics", newValue);
    };

    return (
      <div className="setting-group strict-diacritics">
        <div className="setting">
          <div className="header">{strictDiacriticsCopy.title}</div>
          <div className="description">({strictDiacriticsCopy.subtitle})</div>
        </div>
        <div className="setting-controls">
          <div aria-label={strictDiacriticsCopy.a11yLabel} className="btn-group" role="group">
            {["on", "off"].map((v) => (
              <button
                className={"btn " + (value === v ? "btn-success active" : "btn-default")}
                key={v}
                onClick={() => toggle(v)}
                type="button"
                value={v}
              >
                {v === "on" ? "On" : "Off"}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderClearAfterAnswering() {
    const { keyDownClearsTextInputAfterAnswering } = this.state;

    return (
      <div className="setting-group clear-after-answering">
        <div className="setting">
          <div className="header">Clear After Answering</div>
          <div className="description">(keypress clears the text input after answering so you can practice re-typing the answer)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('keyDownClearsTextInputAfterAnswering', 'on', keyDownClearsTextInputAfterAnswering, 'On')}
            {this.renderBtnGroupBtn('keyDownClearsTextInputAfterAnswering', 'off', keyDownClearsTextInputAfterAnswering, 'Off')}
          </div>
        </div>
      </div>
    );
  }


  renderColorHint() {
    const typingColorHint = this.state.typingColorHint;
    return (
      <div className="setting-group color-hint">
        <div className="setting">
          <div className="header">Typing Color Hint</div>
          <div className="description">(see if you're entering the correct answer as you type)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('typingColorHint', 'on', typingColorHint, 'On')}
            {this.renderBtnGroupBtn('typingColorHint', 'off', typingColorHint, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderEnterSubmitsEmpty() {
    const { enterSubmitsEmpty } = this.state;
    return (
      <div className="setting-group enter-submit-empty">
        <div className="setting">
          <div className="header">Enter Submits Empty</div>
          <div className="description">(pressing Enter when the input is empty will submit a blank answer)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('enterSubmitsEmpty', 'on', enterSubmitsEmpty, 'On')}
            {this.renderBtnGroupBtn('enterSubmitsEmpty', 'off', enterSubmitsEmpty, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderTextBoxSize() {
    const { fitTextInputWidth } = this.state;
    return (
      <div className="setting-group">
        <div className="setting">
          <div className="header">Text Box Size</div>
          <div className="description">(text box size can change to match the missing word)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('fitTextInputWidth', 'on', fitTextInputWidth, 'Changes')}
            {this.renderBtnGroupBtn('fitTextInputWidth', 'off', fitTextInputWidth, 'Always the Same')}
          </div>
        </div>
      </div>
    );
  }

  renderAudioRecordings() {
    const { audioRecordingsAvailable } = this.props;
    const { audioRecordings } = this.state;

    if(!audioRecordingsAvailable) {
      return null;
    }

    return (
      <div className="setting-group audio-recordings" style={{ display: "block" }}>
        <div className="setting">
          <div className="header">Prefer Audio Recordings</div>
          <div className="description">(when available, otherwise will fallback to text-to-speech if available)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('audioRecordings', 'on', audioRecordings, 'On')}
            {this.renderBtnGroupBtn('audioRecordings', 'off', audioRecordings, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderTextToSpeech() {
    const { clozemasterTtsAvailable, systemTtsAvailable, systemTtsVoices } = this.props;
    const { clozemasterTextToSpeech, textToSpeech, textToSpeechSpeed, textToSpeechVoice, textToSpeechLooping } = this.state;
    return (
      <TextToSpeechSettings
        clozemasterTextToSpeech={clozemasterTextToSpeech}
        clozemasterTtsAvailable={clozemasterTtsAvailable}
        onSpeedChanged={(speed) => this.updateSetting('textToSpeechSpeed', speed)}
        onVoiceSelected={(voice) => this.updateSetting('textToSpeechVoice', voice)}
        renderBtnGroupBtn={(...args) => this.renderBtnGroupBtn(...args)}
        systemTtsAvailable={systemTtsAvailable}
        systemTtsVoices={systemTtsVoices}
        textToSpeech={textToSpeech}
        textToSpeechLooping={textToSpeechLooping}
        textToSpeechSpeed={textToSpeechSpeed}
        textToSpeechVoice={textToSpeechVoice}
      />
    );
  }

  renderAccentShortcutsToggle() {
    return (
      <div className="setting-group">
        <div className="setting">
          <div className="header">Accent Shortcuts</div>
          <div className="description">alt+letter to insert accented version of letter pressed. Press letter multiple times while holding alt key for different available accents. May conflict with system shortcuts.</div>
        </div>
        <div className="setting-controls">
          <div className="checkbox">
            <label>
              <input type="checkbox" name="game_settings_accent_shortcuts" id="game_settings_accent_shortcuts" checked={this.state.accentShortcuts === 'on'} onChange={(e) => this.updateSetting('accentShortcuts', e.target.checked ? 'on' : 'off')} />
              Enabled
            </label>
          </div>
        </div>
      </div>
    );
  }

  renderShortcutHotkeysList() {
    return (
      <>
        <div className="setting-group">
          <div className="setting">
            <div className="header">Shortcut Hotkeys</div>
          </div>
          <div className="setting-controls">
            <div className="checkbox">
              <label>
                <input type="checkbox" name="game_settings_hotkeys" id="game_settings_hotkeys" value="1" checked={this.state.hotkeys === 'on'} onChange={(e) => this.updateSetting('hotkeys', e.target.checked ? 'on' : 'off')} /> Enabled
              </label>
            </div>
          </div>
        </div>
        <ShortcutsList mode={this.props.shortcutsMode} />
      </>
    );
  }

  renderDarkMode() {
    const { theme } = this.state;

    return (
      <div className="setting-group dark-mode">
        <div className="setting">
          <div className="header">Dark Mode</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('theme', 'dark', theme, 'On')}
            {this.renderBtnGroupBtn('theme', 'light', theme, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderLeveledUpNotificationsToggle() {
    if(!this.props.isSignedIn) {
      return null;
    }

    const { leveledUpNotifications } = this.state;

    return (
      <div className="setting-group leveled-up-notifications">
        <div className="setting">
          <div className="header">Leveled Up Notifications</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('leveledUpNotifications', 'on', leveledUpNotifications, 'On')}
            {this.renderBtnGroupBtn('leveledUpNotifications', 'off', leveledUpNotifications, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderFontSizes() {
    const {
      hintFontSize,
      notesFontSize,
      pronunciationFontSize,
      transliterationFontSize,
      translationFontSize,
    } = this.state;

    return (
      <div className="setting-group font-sizes">
        <div className="setting">
          <div className="header">Font Sizes</div>
        </div>
        <div className="setting-controls" style={{ flexDirection: "column" }}>
          <FontSizer
            label="Hint"
            onChange={(value) => this.updateSetting("hintFontSize", value)}
            value={hintFontSize}
          />
          <FontSizer
            label="Notes"
            onChange={(value) => this.updateSetting("notesFontSize", value)}
            value={notesFontSize}
          />
          <FontSizer
            label="Pronunciation"
            onChange={(value) => this.updateSetting("pronunciationFontSize", value)}
            value={pronunciationFontSize}
          />
          <FontSizer
            label="Translation"
            onChange={(value) => this.updateSetting("translationFontSize", value)}
            value={translationFontSize}
          />
          <FontSizer
            label="Transliteration"
            onChange={(value) => this.updateSetting("transliterationFontSize", value)}
            value={transliterationFontSize}
          />
        </div>
      </div>
    );

    // return (
    //   <>
    //     {this.renderTranslationFontSize()}
    //     {this.renderHintFontSize()}
    //     {this.renderNotesFontSize()}
    //   </>
    // );
  }

  renderTranslationFontSize() {
    const { translationFontSize } = this.state;

    return (
      <div className="setting-group translation-font-size">
        <div className="setting">
          <div className="header">Translation Font Size</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('translationFontSize', '1x', translationFontSize, '1x')}
            {this.renderBtnGroupBtn('translationFontSize', '1-5x', translationFontSize, '1.5x')}
            {this.renderBtnGroupBtn('translationFontSize', '2x', translationFontSize, '2x')}
            {this.renderBtnGroupBtn('translationFontSize', '3x', translationFontSize, '3x')}
          </div>
        </div>
      </div>
    );

  }

  renderHintFontSize() {
    const { hintFontSize } = this.state;

    return (
      <div className="setting-group hint-font-size">
        <div className="setting">
          <div className="header">Hint Font Size</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('hintFontSize', '1x', hintFontSize, '1x')}
            {this.renderBtnGroupBtn('hintFontSize', '1-5x', hintFontSize, '1.5x')}
            {this.renderBtnGroupBtn('hintFontSize', '2x', hintFontSize, '2x')}
            {this.renderBtnGroupBtn('hintFontSize', '3x', hintFontSize, '3x')}
          </div>
        </div>
      </div>
    );
  }

  renderNotesFontSize() {
    const { notesFontSize } = this.state;

    return (
      <div className="setting-group notes-font-size">
        <div className="setting">
          <div className="header">Notes Font Size</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('notesFontSize', '1x', notesFontSize, '1x')}
            {this.renderBtnGroupBtn('notesFontSize', '1-5x', notesFontSize, '1.5x')}
            {this.renderBtnGroupBtn('notesFontSize', '2x', notesFontSize, '2x')}
            {this.renderBtnGroupBtn('notesFontSize', '3x', notesFontSize, '3x')}
          </div>
        </div>
      </div>
    );
  }

  renderManualMasterResetConfirmToggle() {
    if(!this.props.isSignedIn) {
      return null;
    }

    const { manualMasterResetConfirm } = this.state;


    return (
      <div className="setting-group manual-master-reset-confirm">
        <div className="setting">
          <div className="header">Manual Master/Reset Confirmation</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('manualMasterResetConfirm', 'on', manualMasterResetConfirm, 'On')}
            {this.renderBtnGroupBtn('manualMasterResetConfirm', 'off', manualMasterResetConfirm, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderListeningSkillAutoShowSentence() {
    const { listeningSkillAutoShowSentence } = this.state;
    const { isPlayingListening } = this.props;

    if(!isPlayingListening) {
      return null;
    }

    return (
      <div className="setting-group listening-auto-show">
        <div className="setting">
          <div className="header">Automatically Show Sentence After First Listen</div>
          <div className="description">(when playing listening skill)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('listeningSkillAutoShowSentence', 'on', listeningSkillAutoShowSentence, 'On')}
            {this.renderBtnGroupBtn('listeningSkillAutoShowSentence', 'off', listeningSkillAutoShowSentence, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderMorePracticeControls() {
    const { morePracticeControls } = this.state;

    return (
      <div className="setting-group more-practice-controls">
        <div className="setting">
          <div className="header">More Practice</div>
          <div className="description">(Write it and Speak it buttons after answering, or alt+w / alt+v)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn("morePracticeControls", "on", morePracticeControls, "On")}
            {this.renderBtnGroupBtn("morePracticeControls", "off", morePracticeControls, "Off")}
          </div>
        </div>
      </div>
    );
  }

  renderTransliterationToggle() {
    const { transliterationsAvailable } = this.props;
    const { transliteration } = this.state;

    if(!transliterationsAvailable) {
      return null;
    }

    return (
      <div className="setting-group transliteration" style={{ display: 'block' }}>
        <div className="setting">
          <div className="header">Transliteration</div>
          <div className="description">(shown after answering when available)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            {this.renderBtnGroupBtn('transliteration', 'on', transliteration, 'On')}
            {this.renderBtnGroupBtn('transliteration', 'off', transliteration, 'Off')}
          </div>
        </div>
      </div>
    );
  }

  renderSentenceTextInitiallyHidden() {
    const { sentenceTextInitiallyHidden } = this.state;

    return (
      <div className="setting-group sentence-text-initially-hidden">
        <div className="setting">
          <div className="header">Sentence Text Initially Hidden</div>
          <div className="description">(see just the translation, then click a button to see the sentence text)</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" data-toggle="buttons">
            <button className={`btn ${sentenceTextInitiallyHidden === "on" ? "btn-success active" : "btn-default"}`} onClick={() => this.updateSetting({ sentenceTextInitiallyHidden: "on", translations: "visible" })} type="button" value="on">On</button>
            <button className={`btn ${sentenceTextInitiallyHidden === "off" ? "btn-success active" : "btn-default"}`} onClick={() => this.updateSetting({ sentenceTextInitiallyHidden: "off" })} type="button" value="off">Off</button>
          </div>
        </div>
      </div>
    );
  }

  // Sections are built from keyed entries so that one whose every setting has been
  // conditioned away disappears entirely, rather than leaving a heading over an empty
  // grid. Translate empties Gameplay outright, and this keeps that from having to be
  // tracked by hand as more conditions land.
  renderSettingsSection(header, entries) {
    const shown = entries.filter(([, setting]) => !!setting);

    if(!shown.length) {
      return null;
    }

    return (
      <div className="settings-section">
        <h5 className="joystix settings-section-header">{header}</h5>
        <div className="settings-section-grid">
          {shown.map(([key, setting]) => <React.Fragment key={key}>{setting}</React.Fragment>)}
        </div>
      </div>
    );
  }

  renderTranslations() {
    // Translate shows the translation as the prompt, so there is nothing to choose.
    // Transcribe still hides it, which is most of the point.
    if(this.props.isTranslate) {
      return null;
    }

    const { translations } = this.state;

    return (
      <div className="setting-group translations">
        <div className="setting">
          <div className="header">Translations</div>
        </div>
        <div className="setting-controls">
          <div className="btn-group" role="group" aria-label="translations">
            {this.renderBtnGroupBtn('translations', 'visible', translations, 'Visible')}
            {this.renderBtnGroupBtn('translations', 'show-after', translations, <span><span className="hidden-xs">Show </span>After Answering</span>)}
            {this.renderBtnGroupBtn('translations', 'hidden', translations, 'Hidden')}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      imageBackground,
      imageToggle,
      pronunciation,
      soundEffects,
      transliteration
    } = this.state;

    // isPlayingFullTextInput: typing the whole sentence, so several settings below have
    // nothing to act on. isTranslate is read by renderTranslations.
    const { isPlayingFullTextInput, isPlayingMultipleChoice, isPlayingSpeaking } = this.props;

    return (
      <div className="game-settings text-center">
        {this.renderSettingsSection("Gameplay", [
          ["translations", this.renderTranslations()],
          // The sentence is already masked when typing it in full, and turning this on
          // hides the game controls and steals the first Enter.
          ["sentence-text-initially-hidden", !isPlayingFullTextInput && this.renderSentenceTextInitiallyHidden()],
          // Hints only render in the unanswered branch of renderCurrentSentence, which
          // full text input replaces.
          ["hints", !isPlayingMultipleChoice && !isPlayingFullTextInput && this.renderHints()],
          // The off-by-N check is gated on text input mode.
          ["spelling-hints", !isPlayingMultipleChoice && !isPlayingFullTextInput && this.renderSpellingHints()],
          ["listening-auto-show", this.renderListeningSkillAutoShowSentence()]
        ])}

        {!isPlayingMultipleChoice && this.renderSettingsSection("Text Input", [
          // Typed answers only. Mobile puts it between spelling hints and the colour
          // hint; here that is the top of this section. Speaking is never strict.
          ["strict-diacritics", !isPlayingSpeaking && this.renderStrictDiacritics()],
          ["color-hint", this.renderColorHint()],
          // Sizes the box to the missing word; the full-sentence textarea has its own
          // fixed width.
          ["text-box-size", !isPlayingFullTextInput && this.renderTextBoxSize()],
          ["enter-submits-empty", this.renderEnterSubmitsEmpty()],
          // Reached only through onTextInputKeyDown, which belongs to the single-word
          // input.
          ["clear-after-answering", !isPlayingFullTextInput && this.renderClearAfterAnswering()],
          ["accent-shortcuts", this.renderAccentShortcutsToggle()]
        ])}

        {/* Audio */}
        <div className="settings-section">
          <h5 className="joystix settings-section-header">Audio</h5>
          <div className="settings-section-grid">
            {this.renderAudioRecordings()}
            <div className="setting-group">
              <div className="setting">
                <div className="header">Sound Effects</div>
              </div>
              <div className="setting-controls">
                <div className="btn-group" data-toggle="buttons">
                  {this.renderBtnGroupBtn('soundEffects', 'on', soundEffects, 'On')}
                  {this.renderBtnGroupBtn('soundEffects', 'off', soundEffects, 'Off')}
                </div>
              </div>
            </div>
            {this.renderTextToSpeech()}
          </div>
        </div>

        {/* Display */}
        <div className="settings-section">
          <h5 className="joystix settings-section-header">Display</h5>
          <div className="settings-section-grid">
            {this.renderDarkMode()}
            <div className="setting-group image-toggle">
              <div className="setting">
                <div className="header">Image Toggle</div>
                <div className="description">(toggle button, if sentence image available)</div>
              </div>
              <div className="setting-controls">
                <div className="btn-group" role="group" aria-label="translations">
                  {this.renderBtnGroupBtn('imageToggle', 'after', imageToggle, 'After Answering')}
                  {this.renderBtnGroupBtn('imageToggle', 'always', imageToggle, 'Before and After')}
                  {this.renderBtnGroupBtn('imageToggle', 'off', imageToggle, 'Off')}
                </div>
              </div>
            </div>
            <div className="setting-group image-background">
              <div className="setting">
                <div className="header">Image Background</div>
                <div className="description">(use sentence image as background, if available)</div>
              </div>
              <div className="setting-controls">
                <div className="btn-group" role="group" aria-label="translations">
                  {this.renderBtnGroupBtn('imageBackground', 'on', imageBackground, 'On')}
                  {this.renderBtnGroupBtn('imageBackground', 'off', imageBackground, 'Off')}
                </div>
              </div>
            </div>
            <div className="setting-group pronunciation">
              <div className="setting">
                <div className="header">Pronunciation</div>
                <div className="description">(shown after answering when available)</div>
              </div>
              <div className="setting-controls">
                <div className="btn-group" data-toggle="buttons">
                  {this.renderBtnGroupBtn('pronunciation', 'on', pronunciation, 'On')}
                  {this.renderBtnGroupBtn('pronunciation', 'off', pronunciation, 'Off')}
                </div>
              </div>
            </div>
            {this.renderTransliterationToggle()}
            {this.renderFontSizes()}
          </div>
        </div>

        {/* Advanced */}
        <div className="settings-section">
          <h5 className="joystix settings-section-header">Advanced</h5>
          <div className="settings-section-grid">
            {this.renderMorePracticeControls()}
            {this.renderLeveledUpNotificationsToggle()}
            {this.renderManualMasterResetConfirmToggle()}
            {this.renderShortcutHotkeysList()}
          </div>
        </div>
      </div>
    );
  }
}
